<!-- Returns and inflation: nominal and real return by period, value of 100 zł, rolling return, summaries. -->
<script lang="ts">
  import * as E from "../core/engine";
  import * as X from "../core/analysis";
  import type { Granularity, PeriodicReturn } from "../core/types";
  import { app, need, PERIOD_NAMES } from "../lib/state.svelte";
  import { download, toCsv, type CsvCol } from "../lib/actions";
  import { fmtDate, fmtNum, pctPlain, rangeLabel } from "../lib/format";
  import { baseOption, lineSeries, palette, series, timeAxis, tooltipRows, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Seg from "../components/Seg.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const net = $derived(!!app.tax);
  const gran = $derived<Granularity>(app.ui.retGran || "month");
  const cpi = $derived(m.cpi as Float64Array);
  const rows = $derived(E.periodicReturns(m, A, gran, net));
  const r = $derived(app.range);
  // charts follow the period filter at the top; skip a few-day "tail" of a month at the start of the range
  const chartRows = $derived(E.periodicReturns(m, A, gran, net, r).filter((x, k, arr) => !(k === 0 && arr.length > 1 && x.clipped && x.e - x.s0 < 3)));
  const st = $derived(E.periodStats(m, A, r.start, r.end));
  const infl = $derived(E.inflationBetween(m, st.s0, st.e) as number);
  const nom = $derived(net ? st.twrNet : st.twr);
  const real = $derived((1 + nom) / (1 + infl) - 1);
  const yrs = $derived(st.days / 365);
  const ann = (x: number) => (yrs >= 1 ? Math.pow(1 + x, 1 / yrs) - 1 : null);
  const rg = $derived((E.realGain(m, A, st.s0, st.e) as number) - (net ? st.taxChange : 0));
  const GL = [["month", "Miesięcznie"], ["quarter", "Kwartalnie"], ["year", "Rocznie"]] as const;
  const gl: Record<Granularity, string> = { month: "Miesięcznie", quarter: "Kwartalnie", year: "Rocznie" };
  const cpiNote = $derived(m.cpiLast ? `inflacja GUS do ${m.cpiLast.slice(5)}.${m.cpiLast.slice(0, 4)}; dla późniejszych dni przyjęto brak zmian cen` : "");
  const annNom = $derived(ann(nom));
  const annInfl = $derived(ann(infl));
  const annReal = $derived(ann(real));

  const roll = $derived(X.rollingReturns(m, A, 365, net).filter((x) => x.day >= r.start && x.day <= r.end));
  const rollReal = $derived(roll.filter((x) => x.real !== null) as (typeof roll[number] & { real: number })[]);
  const beat = $derived(rollReal.length ? rollReal.filter((x) => x.real > 0).length / rollReal.length : null);
  const rollHint = $derived(`w każdym dniu: zwrot za poprzednie 12 miesięcy · portfel wyprzedzał inflację w ${beat !== null ? fmtNum(beat * 100, 0) + "%" : "–"} dni wybranego okresu · najgorszy realnie ${rollReal.length ? pctPlain(Math.min(...rollReal.map((x) => x.real))) : "–"}, najlepszy ${rollReal.length ? pctPlain(Math.max(...rollReal.map((x) => x.real))) : "–"}`);
  const yearRows = $derived(gran === "year" ? rows : E.periodicReturns(m, A, "year", net));

  const retOpt = $derived.by(() => {
    const p = palette();
    const labels = chartRows.map((x) => x.label + (x.clipped && !x.partial ? "*" : ""));
    const pctFmt = (v: number) => fmtNum(v * 100, Math.abs(v) < 0.1 ? 1 : 0) + "%";
    const bar = (name: string, key: "nominal" | "real", color: string) => ({ name, type: "bar", barMaxWidth: 22, barGap: "10%", data: chartRows.map((x) => { const v = x[key]; return v === null ? null : +v.toFixed(5); }), itemStyle: { color, borderRadius: [3, 3, 0, 0] } });
    return {
      ...baseOption(p),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => pctPlain(v, 2)) },
      xAxis: { type: "category", data: labels, axisLabel: { color: p.muted, hideOverlap: true }, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } } },
      yAxis: valueAxis(pctFmt, p),
      series: [bar("Zwrot nominalny", "nominal", series(0)), bar("Zwrot realny", "real", series(1)),
        { name: "Inflacja", type: "line", data: chartRows.map((x) => +(x.inflation as number).toFixed(5)), showSymbol: chartRows.length < 30, symbolSize: 6, lineStyle: { width: 2, color: p.muted }, itemStyle: { color: p.muted } }],
    };
  });
  // cumulative: from the first day with a value
  const cumOpt = $derived.by(() => {
    const p = palette();
    const first = st.s0, e = st.e;
    const idxN = E.twrIndex(A, first, e, net);
    const d = m.days.slice(first, e + 1);
    const c0 = cpi[first];
    return {
      ...baseOption(p), tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => fmtNum(v, 2) + " zł") },
      xAxis: timeAxis(p), yAxis: valueAxis((v) => fmtNum(v, 0), p, { scale: true }),
      series: [
        lineSeries("Portfel nominalnie", d.map((x, k) => [x, +(100 * idxN[k]).toFixed(3)]), series(0)),
        lineSeries("Portfel realnie", d.map((x, k) => [x, +((100 * idxN[k] * c0) / cpi[first + k]).toFixed(3)]), series(1)),
        lineSeries("Koszyk cen (inflacja)", d.map((x, k) => [x, +((100 * cpi[first + k]) / c0).toFixed(3)]), p.muted),
      ],
    };
  });
  const rollOpt = $derived.by(() => {
    const p = palette();
    const pctF = (v: number) => fmtNum(v * 100, 0) + "%";
    return {
      ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis(pctF, p),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => pctPlain(v, 1)) },
      series: [
        lineSeries("Zwrot nominalny 12M", roll.map((x) => [x.day, +x.nominal.toFixed(5)]), series(0)),
        lineSeries("Zwrot realny 12M", roll.map((x) => [x.day, x.real === null ? null : +x.real.toFixed(5)]), series(1), { areaStyle: { color: series(1), opacity: 0.08 } }),
        lineSeries("Inflacja r/r", roll.map((x) => [x.day, x.inflation === null ? null : +x.inflation.toFixed(5)]), p.muted, { lineStyle: { width: 1.5, type: "dashed", color: p.muted } }),
      ],
    };
  });

  const retCols: CsvCol<PeriodicReturn>[] = [
    { k: "label", label: "okres" }, { k: "start" }, { k: "end" }, { label: "zwrot_nominalny", v: (x) => x.nominal.toFixed(6) },
    { label: "inflacja", v: (x) => (x.inflation as number).toFixed(6) }, { label: "zwrot_realny", v: (x) => (x.real as number).toFixed(6) },
    { label: "wynik_pln", v: (x) => x.pl.toFixed(2) }, { label: "zysk_ponad_inflacje_pln", v: (x) => x.realPl.toFixed(2) },
    { label: "wplaty_netto", v: (x) => x.flows.toFixed(2) }, { label: "wartosc_poczatek", v: (x) => x.startValue.toFixed(2) },
    { label: "wartosc_koniec", v: (x) => x.endValue.toFixed(2) },
  ];
</script>

{#snippet retTable(list: readonly PeriodicReturn[], first: string)}
  <!-- {#key}: table rebuilt from scratch when data changes (as in the old view) — with in-place updates Chrome may lay out columns differently (by fractions of a pixel) -->
  {#key list}<div class="table-wrap"><table><thead><tr><th class="l">{first}</th><th>Zwrot nominalny</th><th>Inflacja</th><th>Zwrot realny</th><th>Wynik</th><th>Zysk ponad inflację</th><th>Wpłaty netto</th><th>Wartość na początku</th><th>Wartość na koniec</th></tr></thead>
    <tbody>
      {#each list.slice().reverse() as x, i (i)}
        <tr><td class="l"><b>{x.label}</b>{#if x.firstFrom}{" "}<span class="badge">od {fmtDate(x.firstFrom).slice(0, 5)}</span>{/if}{#if x.partial}{" "}<span class="badge">w toku</span>{/if}{#if !x.cpiKnown}{" "}<span class="badge warn">inflacja niepełna</span>{/if}</td>
          <td><Pct v={x.nominal} d={2} /></td><td><Pct v={x.inflation} d={2} sign={false} /></td><td><Pct v={x.real} d={2} /></td><td><Signed v={x.pl} /></td><td><Signed v={x.realPl} /></td><td><Money v={x.flows} /></td><td><Money v={x.startValue} /></td><td><Money v={x.endValue} /></td></tr>
      {/each}
    </tbody>
  </table></div>{/key}
{/snippet}

{#if !m.cpi}
  <ViewHead title="Zwroty i inflacja" />
  <div class="notice">Brak danych o inflacji GUS (plik data/inflation/cpi.csv). Aplikacja pobierze je automatycznie przy następnym odświeżeniu notowań.</div>
{:else}
  <ViewHead title="Zwroty i inflacja" sub="{rangeLabel(r)}{net ? ' · po podatku Belki' : ''} · {cpiNote}" />
  <div class="kpis">
    <Kpi label="Zwrot nominalny: {PERIOD_NAMES[app.period]}">
      {#snippet value()}<Pct v={nom} />{/snippet}
      {#snippet sub()}{#if annNom !== null}<Pct v={annNom} /> rocznie{:else}TWR{/if}{/snippet}
    </Kpi>
    <Kpi label="Inflacja w tym czasie" sub={annInfl !== null ? `${pctPlain(annInfl)} rocznie` : "CPI, GUS"}>{#snippet value()}<Pct v={infl} d={1} sign={false} />{/snippet}</Kpi>
    <Kpi label="Zwrot realny (po inflacji)">
      {#snippet value()}<Pct v={real} />{/snippet}
      {#snippet sub()}{#if annReal !== null}<Pct v={annReal} /> rocznie{:else}siła nabywcza{/if}{/snippet}
    </Kpi>
    <Kpi label="Zysk ponad inflację" sub="w złotówkach z końca okresu">{#snippet value()}<Signed v={rg} />{/snippet}</Kpi>
    <Kpi label="Wynik nominalny" sub="bez uwzględnienia inflacji">{#snippet value()}<Signed v={net ? st.net : st.gross} />{/snippet}</Kpi>
  </div>
  <Card title="Zwrot nominalny i realny · {rangeLabel(r)}" hint="TWR — niezależny od wpłat i wypłat; realny = (1 + nominalny) / (1 + inflacja) − 1; * okres przycięty do wybranego zakresu">
    {#snippet extra()}<Seg id="granSeg" attr="g" options={GL} value={gran} onselect={(k) => (app.ui.retGran = k)} />{/snippet}
    <Chart id="chRet" class="tall" option={retOpt} />
  </Card>
  <Card title="Wartość 100 zł zainwestowanych {fmtDate(r.start)}" hint="portfel nominalnie, portfel realnie (w cenach z początku okresu) i koszyk cen"><Chart id="chCum" option={cumOpt} /></Card>
  {#if roll.length}
    <Card title="Zwrot kroczący 12-miesięczny" hint={rollHint}><Chart id="chRoll" option={rollOpt} /></Card>
  {/if}
  <Card title="Rocznie: zestawienie" hint="pierwszy rok liczony od pierwszej wpłaty, bieżący — do dziś">
    {#snippet extra()}<button class="btn" id="expRetY" onclick={() => download("zwroty_roczne.csv", toCsv(yearRows, retCols))}>Eksport CSV</button>{/snippet}
    {@render retTable(yearRows, "Rok")}
  </Card>
  {#if gran !== "year"}
    <Card title="{gl[gran]}: zestawienie">
      {#snippet extra()}<button class="btn" id="expRet" onclick={() => download(`zwroty_${gran}.csv`, toCsv(rows, retCols))}>Eksport CSV</button>{/snippet}
      {@render retTable(rows, "Okres")}
    </Card>
  {/if}
{/if}
